import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, PackageOpen } from 'lucide-react';

import { Button } from '@/components/Common/button';
import { usePermissions } from '@/hooks/usePermissions';
import notify from '@/lib/notify';
import { coworkService } from '@/services/coworkService';

interface CoWorkHandoffButtonProps {
  useCaseId: string;
  useCaseName?: string;
}

/**
 * Creates a CoWork handoff package from a use case and hands over to the
 * CoWork page, which auto-starts the generation stream for the new package.
 */
export const CoWorkHandoffButton: React.FC<CoWorkHandoffButtonProps> = ({
  useCaseId,
  useCaseName,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canRead } = usePermissions();
  const [isPreparing, setIsPreparing] = useState(false);

  const handleClick = useCallback(async () => {
    setIsPreparing(true);
    try {
      const handoffPackage = await coworkService.createFromUseCase({
        use_case_id: useCaseId,
        title: useCaseName,
      });
      navigate(`/cowork?package=${handoffPackage.id}`);
    } catch (error) {
      console.error('Failed to prepare CoWork handoff:', error);
      notify.error(t('cowork.errors.createFromUseCase'));
    } finally {
      setIsPreparing(false);
    }
  }, [navigate, t, useCaseId, useCaseName]);

  if (!canRead('cowork')) return null;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={isPreparing}
      onClick={() => void handleClick()}
    >
      {isPreparing ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <PackageOpen className="mr-2 h-4 w-4" />
      )}
      {t('cowork.actions.prepareFromUseCase')}
    </Button>
  );
};

export default CoWorkHandoffButton;
